import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Container,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { 
  useServiceOverviewQuery, 
  useClientInsightsQuery, 
  useContractAnalysisQuery 
} from '../../hooks/useDashboardQuery';
import DashboardFilters, { FilterOptions } from './components/DashboardFilters';
import { ServiceTypeDistribution } from './components/ServiceTypeDistribution';
import { ContractValueDistribution } from './components/ContractValueDistribution';
import { ContractRenewalTimeline } from './components/ContractRenewalTimeline';
import BusinessTypeDistribution from './components/BusinessTypeDistribution';
import IndustrySectorDistribution from './components/IndustrySectorDistribution';
import ClientAcquisitionTimeline from './components/ClientAcquisitionTimeline';
import ContractExpiryForecast from './components/ContractExpiryForecast';
import ClientCompleteness from './components/ClientCompleteness';
import { ClientRevenueAnalysis } from './components/ClientRevenueAnalysis';
import { GeographicDistribution as GeographicDistributionComponent } from './components/GeographicDistribution';
import { ServicePenetrationAnalysis } from './components/ServicePenetrationAnalysis';
import DataQualityOverview from './components/DataQualityOverview';
import DataQualityTab from './components/DataQualityTab';
import { QuoteConversionMetricsComponent } from '../../components/Dashboard/QuoteConversionMetrics';
import CommissionTracker from './components/CommissionTracker';
import SubBrokerOverview from './components/SubBrokerOverview';
import ActiveUnits from './components/ActiveUnits';
import {
  ServiceTypeCount,
  UtilityService
} from '../../types/utility.types';
import { LookupType } from '../../types/lookup.types';
import * as lookupApi from '../../api/lookups';
import { StatusOverviewChart } from './components/StatusOverviewChart';
import { CostOverviewChart } from './components/CostOverviewChart';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

const UtilityDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)),
    endDate: new Date(),
    serviceTypes: []
  });
  const [renewalsPage, setRenewalsPage] = useState(1);
  const [renewalsLimit, setRenewalsLimit] = useState(10);

  // Use React Query hooks for data fetching with automatic caching
  const serviceOverviewQuery = useServiceOverviewQuery(filters, renewalsPage, renewalsLimit);
  const clientInsightsQuery = useClientInsightsQuery(filters, tabValue === 1);
  const contractAnalysisQuery = useContractAnalysisQuery(filters, tabValue === 2);

  // Extract data from queries
  const dashboardData = serviceOverviewQuery.data?.summary || null;
  const costsOverview = serviceOverviewQuery.data?.costs || [];
  const statusOverview = serviceOverviewQuery.data?.status || [];
  const contractValueData = serviceOverviewQuery.data?.contractValue || [];
  const activeUnitsData = serviceOverviewQuery.data?.activeUnits || null;
  
  const clientRevenueData = clientInsightsQuery.data?.clientRevenue || [];
  const servicePenetrationData = clientInsightsQuery.data?.servicePenetration || null;
  const geographicDistribution = clientInsightsQuery.data?.geographic || [];
  const clientCompleteness = clientInsightsQuery.data?.completeness || [];
  
  const contractExpiryForecast = contractAnalysisQuery.data?.contractExpiry || [];

  const loading = serviceOverviewQuery.isLoading;
  const error = serviceOverviewQuery.error ? 
    (serviceOverviewQuery.error as any)?.response?.data?.message || 
    (serviceOverviewQuery.error as Error)?.message || 
    'Failed to load dashboard data' : null;
  const refreshing = serviceOverviewQuery.isFetching && !serviceOverviewQuery.isLoading;

  // Available service types for filtering - fetch from AdminPanel lookups
  const [availableServiceTypes, setAvailableServiceTypes] = useState<string[]>([]);

  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const serviceTypes = await lookupApi.getActiveLookupsByType('ServiceType' as LookupType);
        setAvailableServiceTypes(serviceTypes.map(st => st.value));
      } catch (err) {
        console.error('Failed to load service types:', err);
        // Fallback to empty array - filters will be disabled
        setAvailableServiceTypes([]);
      }
    };
    fetchServiceTypes();
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    setFilters(newFilters);
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [queryClient]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRenewalsPageChange = useCallback((page: number, limit: number) => {
    setRenewalsPage(page);
    setRenewalsLimit(limit);
  }, []);

  if (loading && !refreshing) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Dashboard Data
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !refreshing) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert 
          severity="error" 
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={() => serviceOverviewQuery.refetch()}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>Dashboard Error</Typography>
            <Typography variant="body1" paragraph>
              We encountered an issue while loading the dashboard data. This could be due to:
            </Typography>
            <ul>
              <li>Network connectivity issues</li>
              <li>Server maintenance</li>
              <li>Temporary service disruption</li>
            </ul>
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => serviceOverviewQuery.refetch()}
                startIcon={<RefreshIcon />}
              >
                Reload Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h1">
                Utility Dashboard
              </Typography>
              <Tooltip title="Refresh dashboard data">
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <DashboardFilters 
              onFilterChange={handleFilterChange}
              serviceTypes={availableServiceTypes}
            />
            <Paper sx={{ 
              p: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              color: 'white'
            }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                aria-label="dashboard tabs"
              >
                <Tab label="Service Overview" {...a11yProps(0)} />
                <Tab label="Client Insights" {...a11yProps(1)} />
                <Tab label="Contract Analysis" {...a11yProps(2)} />
                <Tab label="Commission Tracker" {...a11yProps(3)} />
                <Tab label="Sub-Brokers" {...a11yProps(4)} />
                <Tab label="Quote Analytics" {...a11yProps(5)} />
                <Tab label="Data Quality" {...a11yProps(6)} />
                <Tab label="Companies House Audit" {...a11yProps(7)} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ServiceTypeDistribution 
                      data={dashboardData?.servicesByType || {
                        ELECTRICITY: 0,
                        GAS: 0,
                        WATER: 0,
                        TELECOMS: 0,
                        WASTE: 0,
                        MERCHANT_SERVICES: 0,
                        PPA_EXPORT_CONTRACT: 0,
                        EV: 0,
                        RENEWABLE: 0,
                        INSURANCE: 0,
                        NEW_CONNECTION: 0
                      } as ServiceTypeCount}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      color: 'white'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                          Contract Status
                        </Typography>
                        {statusOverview.length > 0 ? (
                          <StatusOverviewChart data={statusOverview} />
                        ) : (
                          <Typography>No status data available</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      color: 'white'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                          Cost Overview
                        </Typography>
                        {costsOverview.length > 0 ? (
                          <CostOverviewChart data={costsOverview} />
                        ) : (
                          <Typography>No cost data available</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ContractValueDistribution data={contractValueData} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ContractRenewalTimeline 
                      services={(dashboardData?.contractsEndingSoon?.data || []) as unknown as UtilityService[]}
                      pagination={dashboardData?.contractsEndingSoon?.pagination}
                      onPageChange={handleRenewalsPageChange}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ActiveUnits 
                      data={activeUnitsData}
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ClientRevenueAnalysis 
                      data={clientRevenueData}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <GeographicDistributionComponent 
                      data={geographicDistribution}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ServicePenetrationAnalysis 
                      data={servicePenetrationData}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <BusinessTypeDistribution 
                      data={dashboardData?.businessTypeDistribution || []}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <IndustrySectorDistribution 
                      data={dashboardData?.industrySectorDistribution || []}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ClientAcquisitionTimeline 
                      data={dashboardData?.clientAcquisitionTimeline || []}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ClientCompleteness 
                      data={clientCompleteness}
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ContractExpiryForecast 
                      data={contractExpiryForecast}
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <CommissionTracker filters={filters} />
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <SubBrokerOverview />
              </TabPanel>

              <TabPanel value={tabValue} index={5}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <QuoteConversionMetricsComponent 
                      loading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <DataQualityOverview 
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={7}>
                <DataQualityTab />
              </TabPanel>

            </Paper>
          </Grid>
        </Grid>
    </Container>
  );
};

export default UtilityDashboard;
